import {
  createLegacySafeSmartAccount,
  createSafeSmartAccount,
  ENTRYPOINT_ADDRESS_V07,
  retrieveAccountAddressFromPasskeys,
  retrieveLegacyWalletAddress,
} from '@cometh/connect-sdk-4337';
import { getAddress } from 'viem';
import { PUBLIC_COMETH_API_KEY, PUBLIC_CONNECT_4337_COMETH_API_KEY } from '$env/static/public';
import type { Address } from '$types/address';
import { WalletStatus } from '$types/walletStatus';
import { publicClient } from '$utils/publicClient';
import { AbiSafe } from '$lib/constants/abis';
import { cometh4337Params, cometh4337SignerConfig } from '$services/cometh/getComethWallet';
import { gnosis } from 'viem/chains';
import { log } from '$utils/__log';

export const migrateSafeWallet = async (address: Address): Promise<boolean> => {
  const { apiKeyLegacy, apiKey4337, chain } = cometh4337Params;

  // Step 1 -  This is the address of you safe using the connect legacy
  const smartAccountAddress = address;
  // Step 2 - Create the legacy ts object to available migration
  const legacyClient = await createLegacySafeSmartAccount({
    apiKeyLegacy,
    apiKey4337,
    chain,
    publicClient: publicClient,
    comethSignerConfig: cometh4337SignerConfig,
    smartAccountAddress: getAddress(address),
    importSafe: false,
  });

  // Step 3 - Migrate the safe
  const tx = await legacyClient.migrate();
  log('Safe Migration tx', tx);

  // Your Safe is now migrated, you can use the 4337 SDK
  // Create the new ts object to handle the upgraded safe
  const updatedSmartAccount = await createSafeSmartAccount({
    apiKey: apiKey4337,
    chain,
    smartAccountAddress,
    entrypoint: ENTRYPOINT_ADDRESS_V07,
  });

  log(updatedSmartAccount, '<-- updatedSmartAccount');

  //Check if the account is updated
  const isTheAccountUpdated = await legacyClient.hasMigrated();
  log('isTheAccountUpdated', isTheAccountUpdated);
  return isTheAccountUpdated;
};

export const isLegacySafeVersion = async (address: Address | null) => {
  if (!address) return false;
  const currentVersion = await getSafeVersion(address);

  if (currentVersion === '1.3.0' || currentVersion === '1.1.1') {
    return true;
  } else {
    return false;
  }
};

export const getSafeVersion = async (address: Address) => {
  const version = await publicClient.readContract({
    address,
    abi: AbiSafe,
    functionName: 'VERSION',
  });
  log(version, 'Safe VERSION');
  return version;
};

export const getLegacySafeAddress = async () => {
  try {
    const legacySafeAddress = (await retrieveLegacyWalletAddress(PUBLIC_COMETH_API_KEY)) as Address;
    return legacySafeAddress;
  } catch (_) {
    return null;
  }
};

export const get4337SafeAddress = async (): Promise<WalletStatus | Address> => {
  try {
    const walletAddress = await retrieveAccountAddressFromPasskeys({
      apiKey: PUBLIC_CONNECT_4337_COMETH_API_KEY,
      chain: gnosis,
      publicClient,
      checkLegacy: true,
    });

    return walletAddress;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error.message.includes('Passkey signer found in legacy DB for this walletAddress')) {
      return WalletStatus.LEGACY_WALLET;
    }
    if (error.message.includes('Signer found is not owner of the wallet')) {
      return WalletStatus.NO_SIGNER;
    }
    //if recovery is in progress && if they try to log with a passkey and it fails
    // direclty fallback to the "recovery in progress" message
    const newPasskeySignerObj = localStorage.getItem('signerObj');
    if (error.message.startsWith('No passkey signer found in db') && newPasskeySignerObj) {
      return WalletStatus.RECOVERY_PROGRESS;
    } else {
      //NotAllowed errors are not handled in cometh sdk, so they will also go here
      return WalletStatus.NO_WALLET;
    }
  }
};

export const waitForSafeVersionUpdate = async (
  safeAddress: Address,
  maxAttempts: number = 5,
  delayMs: number = 1500, //  1.5 seconds delay
): Promise<boolean> => {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const stillLegacy = await isLegacySafeVersion(safeAddress);
    if (!stillLegacy) {
      return true; // Successfully updated
    }
    if (attempt < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  return false; // Timed out, still appears legacy
};
